import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useCurrency } from '../../context/CurrencyContext';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { trackRecommendationEvent } from '../../utils/recommendations';
import { motion } from 'framer-motion';
import { ListLoadingRefreshOverlay } from './PaginatedListLoading';
import { STOREFRONT_STICKY_TOP_STYLE } from '../layout/storefrontLayoutConstants';
import { applyContentLocaleToUrl } from '../../utils/contentLocaleQuery';
import { departmentPath } from '../../utils/departmentRouting';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type PublicCategory = {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  imageUrl?: string | null;
  productImageUrl?: string | null;
};

type DepartmentSummary = { id: string; name: string; slug: string };

type PublicProduct = {
  id: string;
  name: string;
  sku: string;
  imageUrl?: string | null;
  pricePerUnit: number;
  salePrice?: number | null;
  discountPercent?: number | null;
  baseUnit: 'LB' | 'KG';
  currentStockLbs?: number;
  isPreorderEnabled?: boolean;
  preorderEta?: string | null;
  category?: (PublicCategory & { department?: DepartmentSummary | null }) | null;
};

type ProductsResponse = { items: PublicProduct[] };
type CategoriesResponse = PublicCategory[];

type PublicProductsSectionProps = {
  mode?: 'home' | 'catalog';
  initialSearch?: string;
  /** When set (e.g. from `/products?category=slug`), filters the grid to that category. */
  initialCategorySlug?: string;
  /** `null` = global catalog (all departments). */
  departmentSlug: string | null;
  /**
   * When the parent already loaded department copy (multi-dept home), pass through to avoid an extra fetch.
   * If omitted, home mode loads `/departments/public/:slug` for the heading.
   */
  departmentName?: string | null;
  departmentDescription?: string | null;
  /** Shown as “View details” next to the department title (e.g. link to `/${slug}`). */
  departmentDetailPath?: string | null;
};

const pillBaseHome =
  'shrink-0 rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';

const pillCatalogBase =
  'shrink-0 rounded-full px-4 py-2.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 focus-visible:ring-offset-2';

const pillCatalogLightInactive =
  'border border-slate-200/90 bg-white text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50';

const pillCatalogDarkInactive =
  'border border-white/15 bg-white/[0.07] text-slate-100 hover:border-white/25 hover:bg-white/[0.12]';

const pillCatalogActive = 'text-white shadow-lg ring-1 ring-white/15';

function slugToDisplayTitle(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function PublicProductsSection({
  mode = 'catalog',
  initialSearch = '',
  initialCategorySlug = '',
  departmentSlug,
  departmentName: departmentNameProp,
  departmentDescription: departmentDescriptionProp,
  departmentDetailPath,
}: PublicProductsSectionProps) {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { addLine } = useCart();
  const [categories, setCategories] = useState<PublicCategory[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(() =>
    initialCategorySlug.trim() ? initialCategorySlug.trim() : null,
  );
  const [search, setSearch] = useState(initialSearch);
  const [products, setProducts] = useState<PublicProduct[]>([]);
  const [loading, setLoading] = useState(true);
  /** Catalog only: after scrolling, category bar switches to the dark “focused” style. */
  const [catalogBarScrolled, setCatalogBarScrolled] = useState(false);
  const [departmentFromApi, setDepartmentFromApi] = useState<{
    name: string;
    description?: string | null;
  } | null>(null);
  const pageSize = mode === 'home' ? 8 : departmentSlug == null ? 100 : 24;

  const homeDeptName =
    departmentNameProp !== undefined
      ? String(departmentNameProp || '').trim()
      : String(departmentFromApi?.name || '').trim();
  const homeDeptDesc =
    departmentDescriptionProp !== undefined
      ? String(departmentDescriptionProp || '').trim()
      : String(departmentFromApi?.description || '').trim();

  const resolvedHomeHeading =
    homeDeptName ||
    (mode === 'home' && departmentSlug ? slugToDisplayTitle(departmentSlug) : '');

  useEffect(() => {
    if (mode !== 'home' || !departmentSlug || departmentNameProp !== undefined) {
      setDepartmentFromApi(null);
      return;
    }
    setDepartmentFromApi(null);
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          applyContentLocaleToUrl(
            `${API_BASE}/departments/public/${encodeURIComponent(departmentSlug)}`,
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok || cancelled) return;
        const row = (await res.json()) as {
          name?: string;
          description?: string | null;
        };
        if (cancelled) return;
        const n = String(row.name || '').trim();
        if (n) {
          setDepartmentFromApi({
            name: n,
            description: row.description ?? null,
          });
        } else {
          setDepartmentFromApi(null);
        }
      } catch {
        if (!cancelled) setDepartmentFromApi(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, departmentSlug, departmentNameProp, i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    setSearch(initialSearch);
  }, [initialSearch]);

  useEffect(() => {
    const s = initialCategorySlug.trim();
    setSelectedSlug(s ? s : null);
  }, [initialCategorySlug]);

  useEffect(() => {
    if (mode !== 'catalog') return;
    const threshold = 40;
    const onScroll = () => {
      setCatalogBarScrolled(window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [mode]);

  const applyCategorySlug = useCallback(
    (slug: string | null) => {
      setSelectedSlug(slug);
      if (mode !== 'catalog') return;
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          if (slug) n.set('category', slug);
          else n.delete('category');
          return n;
        },
        { replace: true },
      );
    },
    [mode, setSearchParams],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = new URL(`${API_BASE}/catalog/categories`);
        if (departmentSlug) {
          url.searchParams.set('departmentSlug', departmentSlug);
        }
        const res = await fetch(
          applyContentLocaleToUrl(
            url.toString(),
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as CategoriesResponse;
        if (!cancelled) setCategories(data);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [departmentSlug, i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const url = new URL(`${API_BASE}/catalog/products`);
        url.searchParams.set('page', '1');
        url.searchParams.set('pageSize', String(pageSize));
        url.searchParams.set('activeOnly', 'true');
        url.searchParams.set('allowOutOfStock', 'true');
        if (selectedSlug) url.searchParams.set('categorySlug', selectedSlug);
        if (departmentSlug) {
          url.searchParams.set('departmentSlug', departmentSlug);
        }
        const q = search.trim();
        if (q) url.searchParams.set('q', q);
        const res = await fetch(
          applyContentLocaleToUrl(
            url.toString(),
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as ProductsResponse;
        if (!cancelled) setProducts(data.items);
      } catch {
        // ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    departmentSlug,
    pageSize,
    selectedSlug,
    search,
    i18n.language,
    i18n.resolvedLanguage,
  ]);

  const filtered = useMemo(() => products, [products]);

  const catalogDepartmentGroups = useMemo(() => {
    if (mode !== 'catalog' || departmentSlug != null) return null;
    const map = new Map<string, { department: DepartmentSummary; items: PublicProduct[] }>();
    for (const p of filtered) {
      const dept = p.category?.department;
      const slug = dept?.slug || '__';
      if (!map.has(slug)) {
        map.set(slug, {
          department: dept ?? {
            id: '__',
            name: 'Other',
            slug: '__',
          },
          items: [],
        });
      }
      map.get(slug)!.items.push(p);
    }
    return [...map.values()].sort((a, b) =>
      a.department.name.localeCompare(b.department.name),
    );
  }, [mode, departmentSlug, filtered]);

  const selectedCategory = useMemo(
    () => (selectedSlug ? categories.find((c) => c.slug === selectedSlug) : undefined),
    [categories, selectedSlug],
  );

  const hasProducts = products.length > 0;
  const skeletonCount =
    loading && !hasProducts ? Math.min(pageSize, mode === 'home' ? 8 : 12) : 0;

  const brandActiveStyle: React.CSSProperties = {
    backgroundColor: 'var(--storefront-brand-primary, #059669)',
    borderColor: 'var(--storefront-brand-primary, #059669)',
  };

  const CategoryPills = ({
    variant,
    catalogSurface = 'dark',
  }: {
    variant: 'home' | 'catalog';
    catalogSurface?: 'light' | 'dark';
  }) => {
    const ringOffset =
      variant === 'catalog' && catalogSurface === 'light'
        ? 'focus-visible:ring-offset-white'
        : 'focus-visible:ring-offset-slate-900';
    const catalogInactive =
      variant === 'catalog' && catalogSurface === 'light'
        ? pillCatalogLightInactive
        : variant === 'catalog'
          ? pillCatalogDarkInactive
          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50';
    const catalogActiveClass =
      variant === 'catalog' && catalogSurface === 'light' ? 'text-white shadow-md ring-1 ring-black/5' : pillCatalogActive;

    return (
      <div
        className="flex gap-2 overflow-x-auto overscroll-x-contain scroll-smooth pb-0.5 pt-0.5"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <button
          type="button"
          onClick={() => applyCategorySlug(null)}
          className={
            variant === 'catalog'
              ? `${pillCatalogBase} ${ringOffset} ${
                  selectedSlug === null ? catalogActiveClass : catalogInactive
                }`
              : `${pillBaseHome} ${
                  selectedSlug === null
                    ? 'text-white shadow-sm'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                }`
          }
          style={selectedSlug === null ? brandActiveStyle : undefined}
        >
          {t('storefront.products_all')}
        </button>
        {categories.map((c) => {
          const active = selectedSlug === c.slug;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => applyCategorySlug(c.slug)}
              className={
                variant === 'catalog'
                  ? `${pillCatalogBase} ${ringOffset} ${active ? catalogActiveClass : catalogInactive}`
                  : `${pillBaseHome} ${
                      active
                        ? 'text-white shadow-sm'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                    }`
              }
              style={active ? brandActiveStyle : undefined}
            >
              {c.name}
            </button>
          );
        })}
      </div>
    );
  };

  const renderOfferCard = (current: PublicProduct) => {
    const imageSrc =
      current.imageUrl && current.imageUrl.startsWith('http')
        ? current.imageUrl
        : current.imageUrl
          ? `${API_BASE}${current.imageUrl}`
          : undefined;

    const priceInUsd =
      current.salePrice != null ? Number(current.salePrice) : Number(current.pricePerUnit);
    const discountPercent = Math.max(0, Math.min(100, Number(current.discountPercent ?? 0)));
    const discountedPriceInUsd =
      discountPercent > 1e-9
        ? Math.round(priceInUsd * (1 - discountPercent / 100) * 100) / 100
        : priceInUsd;
    const displayPrice = formatPrice(discountedPriceInUsd);
    const oldDisplayPrice = formatPrice(priceInUsd);
    const stock =
      typeof current.currentStockLbs === 'number' ? Math.max(0, current.currentStockLbs) : null;
    const canAddToCart = stock === null || stock > 0;
    const canPreorder = !canAddToCart && Boolean(current.isPreorderEnabled);
    const nextArrivalDateLabel =
      current.preorderEta != null && String(current.preorderEta).trim().length > 0
        ? String(current.preorderEta).slice(0, 10)
        : null;

    const CardTag = mode === 'catalog' ? motion.article : 'article';
    const cardProps =
      mode === 'catalog'
        ? {
            initial: { opacity: 0, y: 14 },
            whileInView: { opacity: 1, y: 0 },
            viewport: { once: true, amount: 0.18 },
            transition: { duration: 0.35 },
          }
        : {};

    return (
      <CardTag
        {...cardProps}
        className={
          mode === 'catalog'
            ? 'group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm transition hover:border-slate-300/90 hover:shadow-md'
            : 'group flex h-full flex-col overflow-hidden rounded-lg border border-slate-200/90 bg-white transition-colors hover:border-slate-300'
        }
      >
        <div className="relative flex-1 bg-slate-50">
          <div className="absolute left-4 top-4 rounded-full bg-[var(--storefront-nav-accent,#ea580c)] px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
            {t('storefront.products_new_badge')}
          </div>
          <div className="flex h-40 items-center justify-center p-4 sm:h-44">
            {imageSrc ? (
              <img src={imageSrc} alt={current.name} className="max-h-full max-w-full object-contain" />
            ) : (
              <div className="h-20 w-20 rounded-full bg-slate-200" />
            )}
          </div>
          {stock !== null && (
            <div
              className={`absolute bottom-3 left-3 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-white ${
                canAddToCart ? 'bg-slate-900/75' : 'bg-amber-700/95'
              }`}
            >
              <span className="block max-w-[8.5rem] truncate sm:max-w-[11rem]">
                {canAddToCart
                  ? current.baseUnit === 'KG'
                    ? t('storefront.cart_stock_line_kg', { qty: stock.toFixed(1) })
                    : t('storefront.products_in_stock_lb', { qty: stock.toFixed(1) })
                  : t('storefront.out_of_stock')}
              </span>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 bg-white px-4 pb-4 pt-3">
          <h3 className="text-sm font-semibold text-slate-900">{current.name}</h3>
          <p className="mt-1 text-sm font-semibold">
            {discountPercent > 1e-9 ? (
              <span className="inline-flex items-baseline gap-2">
                <span className="text-rose-600 line-through">{oldDisplayPrice}</span>
                <span
                  className={mode === 'catalog' ? '' : 'text-[color:var(--storefront-brand-primary,#059669)]'}
                  style={
                    mode === 'catalog'
                      ? { color: 'var(--storefront-brand-primary, #059669)' }
                      : undefined
                  }
                >
                  {displayPrice}
                </span>
              </span>
            ) : (
              <span
                className={mode === 'catalog' ? '' : 'text-[color:var(--storefront-brand-primary,#059669)]'}
                style={
                  mode === 'catalog'
                    ? { color: 'var(--storefront-brand-primary, #059669)' }
                    : undefined
                }
              >
                {displayPrice}
              </span>
            )}
            <span className="ml-1 text-xs font-normal text-slate-500">
              / {current.baseUnit.toLowerCase()}
            </span>
          </p>

          {canPreorder ? (
            <p className="mt-2 text-[11px] text-indigo-700">
              {nextArrivalDateLabel
                ? t('storefront.preorder_next_arrival', {
                    defaultValue: 'Estimated Next arrival: {{date}}',
                    date: nextArrivalDateLabel,
                  })
                : t('storefront.preorder_next_arrival_tbd', {
                    defaultValue: 'Estimated Next arrival: TBA',
                  })}
            </p>
          ) : null}

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] font-medium text-slate-600">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void trackRecommendationEvent({
                  productId: current.id,
                  eventType: 'PRODUCT_VIEW',
                  context: 'catalog_grid',
                  source: 'public_products',
                });
                navigate(departmentPath(departmentSlug, `/products/${current.id}`));
              }}
              className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-[color:var(--storefront-brand-primary,#059669)]"
            >
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: 'var(--storefront-brand-primary, #059669)' }}
              />
              {t('storefront.products_view_detail')}
            </button>
            <button
              type="button"
              disabled={!canAddToCart && !canPreorder}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!canAddToCart && !canPreorder) return;
                addLine({
                  productId: String(current.id),
                  qty: 1,
                  unit: current.baseUnit,
                });
                void trackRecommendationEvent({
                  productId: current.id,
                  eventType: 'ADD_TO_CART',
                  context: 'catalog_grid',
                  source: 'public_products',
                });
              }}
              aria-label={
                canAddToCart || canPreorder
                  ? t('storefront.products_add_to_cart')
                  : t('storefront.out_of_stock')
              }
              className={`relative z-10 inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] ${
                canAddToCart || canPreorder
                  ? 'border-slate-200 text-slate-700 hover:border-[color:var(--storefront-brand-primary,#059669)] hover:text-[color:var(--storefront-brand-primary,#059669)]'
                  : 'cursor-not-allowed border-slate-200 text-slate-400'
              }`}
            >
              <i className="fa fa-shopping-basket" />
              {canAddToCart
                ? t('storefront.products_add_to_cart')
                : canPreorder
                  ? t('storefront.preorder_now', {
                      defaultValue: 'Pre-order now',
                    })
                  : t('storefront.out_of_stock')}
            </button>
          </div>
        </div>
      </CardTag>
    );
  };

  return (
    <section
      className={
        mode === 'catalog'
          ? 'pb-12 pt-2 sm:pb-16 sm:pt-3'
          : 'border-t border-slate-200/70 py-10 sm:py-14'
      }
      style={{ backgroundColor: 'var(--storefront-body-bg, #f1f5f9)' }}
    >
      <div className="mx-auto flex max-w-6xl flex-col px-4 sm:px-6">
        {mode === 'catalog' ? (
          <>
            <nav className="mb-5 text-sm text-slate-500" aria-label={t('storefront.catalog_breadcrumb_label', { defaultValue: 'Breadcrumb' })}>
              <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <li>
                  <Link to="/" className="transition hover:text-slate-800">
                    {t('storefront.catalog_breadcrumb_home', { defaultValue: 'Home' })}
                  </Link>
                </li>
                <li className="text-slate-300" aria-hidden>
                  /
                </li>
                <li>
                  <Link to="/products" className="transition hover:text-slate-800">
                    {t('public.catalog_title')}
                  </Link>
                </li>
                {selectedCategory ? (
                  <>
                    <li className="text-slate-300" aria-hidden>
                      /
                    </li>
                    <li className="font-semibold text-slate-800" aria-current="page">
                      {selectedCategory.name}
                    </li>
                  </>
                ) : null}
              </ol>
            </nav>

            <div
              className={[
                'sticky z-30 transition-[box-shadow] duration-300 ease-out',
                catalogBarScrolled
                  ? 'shadow-[0_16px_48px_-20px_rgba(0,0,0,0.55)]'
                  : 'shadow-[0_4px_24px_-12px_rgba(15,23,42,0.12)]',
              ].join(' ')}
              style={STOREFRONT_STICKY_TOP_STYLE}
            >
              <div
                className={[
                  'overflow-hidden rounded-2xl border transition-[background-color,border-color] duration-300 ease-out',
                  catalogBarScrolled
                    ? 'border-white/10 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950'
                    : 'border-slate-200/90 bg-white/95 shadow-sm backdrop-blur-md',
                ].join(' ')}
              >
                <div
                  className={[
                    'mx-auto max-w-6xl px-4 sm:px-6',
                    catalogBarScrolled ? 'py-2.5 sm:py-3' : 'py-3 sm:py-4',
                  ].join(' ')}
                >
                  <div
                    className={[
                      'border-b border-slate-200/80 pb-3 lg:pb-4',
                      catalogBarScrolled ? 'hidden' : '',
                    ].join(' ')}
                  >
                    <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                      {selectedCategory ? selectedCategory.name : t('public.catalog_title')}
                    </h1>
                    {selectedCategory?.description?.trim() ? (
                      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
                        {selectedCategory.description}
                      </p>
                    ) : null}
                    {search.trim() ? (
                      <p className="mt-3 max-w-2xl rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2 text-xs text-amber-950">
                        {t('storefront.catalog_search_banner', {
                          defaultValue: 'Header search is filtering these results.',
                        })}
                      </p>
                    ) : null}
                  </div>

                  <nav
                    className={catalogBarScrolled ? 'mt-0' : 'mt-3 lg:mt-4'}
                    aria-label={t('public.categories_heading')}
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:gap-y-3">
                      <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                        <p
                          className={[
                            'shrink-0 text-sm font-semibold transition-colors duration-300',
                            catalogBarScrolled ? 'text-white' : 'text-slate-900',
                          ].join(' ')}
                        >
                          {t('public.categories_heading')}
                        </p>
                        <div className="min-w-0 flex-1">
                          <CategoryPills
                            variant="catalog"
                            catalogSurface={catalogBarScrolled ? 'dark' : 'light'}
                          />
                        </div>
                      </div>
                      {search.trim() ? (
                        <p
                          className={[
                            'max-w-md text-xs leading-snug transition-colors duration-300 sm:max-w-xs sm:text-right',
                            catalogBarScrolled ? 'text-slate-400' : 'text-slate-500',
                          ].join(' ')}
                        >
                          {t('storefront.catalog_search_active_hint', {
                            defaultValue: 'Search filter is active — adjust keywords in the header.',
                          })}
                        </p>
                      ) : null}
                    </div>
                  </nav>
                </div>
              </div>
            </div>
          </>
        ) : null}

        {mode === 'home' ? (
          <header className="grid gap-6 pb-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,3fr)] lg:items-start">
            <div className="min-w-0">
              {resolvedHomeHeading ? (
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                      {resolvedHomeHeading}
                    </h2>
                    {homeDeptDesc ? (
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">{homeDeptDesc}</p>
                    ) : null}
                  </div>
                  {departmentDetailPath ? (
                    <Link
                      to={departmentDetailPath}
                      className="shrink-0 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100"
                    >
                      {t('storefront.view_department_details', { defaultValue: 'View details' })}
                    </Link>
                  ) : null}
                </div>
              ) : (
                <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  {t('storefront.products_title')}
                </h2>
              )}
            </div>
            <div className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {t('public.categories_heading')}
              </p>
              <CategoryPills variant="home" />
            </div>
          </header>
        ) : null}

        {mode === 'catalog' ? (
          <div className="mt-8 flex flex-wrap items-end justify-between gap-3 border-b border-slate-200/90 pb-4">
            <p className="text-sm text-slate-600">
              {loading
                ? t('storefront.catalog_loading_results', { defaultValue: 'Loading products…' })
                : hasProducts
                  ? t('storefront.catalog_results_count', {
                      count: products.length,
                      defaultValue: '{{count}} products',
                    })
                  : t('storefront.catalog_no_results', {
                      defaultValue: 'No products match your filters.',
                    })}
            </p>
          </div>
        ) : null}

        <div className={mode === 'catalog' ? 'mt-6' : ''}>
          <div className="relative">
            {loading && hasProducts ? <ListLoadingRefreshOverlay /> : null}
            {skeletonCount > 0 ? (
              <div
                className={[
                  'grid gap-5 sm:grid-cols-2 lg:grid-cols-4',
                  loading && hasProducts ? 'pointer-events-none select-none' : '',
                ].join(' ')}
              >
                {Array.from({ length: skeletonCount }).map((_, idx) => (
                  <article
                    key={`skeleton-${idx}`}
                    className="flex h-full animate-pulse flex-col rounded-2xl bg-slate-50 p-4 shadow-sm"
                  >
                    <div className="flex-1 rounded-2xl bg-slate-100" />
                    <div className="mt-4 h-3 w-24 rounded-full bg-slate-200" />
                    <div className="mt-2 h-3 w-32 rounded-full bg-slate-200" />
                  </article>
                ))}
              </div>
            ) : mode === 'catalog' && catalogDepartmentGroups ? (
              <div
                className={loading && hasProducts ? 'pointer-events-none select-none' : ''}
              >
                {catalogDepartmentGroups.map((group) => (
                  <div key={group.department.slug} className="mt-12 space-y-5 first:mt-0">
                    <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200/90 pb-3">
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
                          {t('storefront.catalog_department_eyebrow', { defaultValue: 'Department' })}
                        </p>
                        <h2 className="mt-1 text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
                          {group.department.name}
                        </h2>
                      </div>
                      <span className="shrink-0 font-mono text-[11px] font-medium text-slate-400">
                        /{group.department.slug}
                      </span>
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                      {group.items.map((current) => (
                        <React.Fragment key={current.id}>{renderOfferCard(current)}</React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={[
                  'grid gap-5 sm:grid-cols-2 lg:grid-cols-4',
                  loading && hasProducts ? 'pointer-events-none select-none' : '',
                ].join(' ')}
              >
                {filtered.map((current) => (
                  <React.Fragment key={current.id}>{renderOfferCard(current)}</React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>

        {mode === 'home' ? (
          <div className="flex justify-center pt-6">
            <button
              type="button"
              onClick={() => navigate(departmentPath(departmentSlug, '/products'))}
              className="rounded-full border border-slate-300 bg-white px-6 py-2 text-sm font-semibold text-slate-800 hover:border-[color:var(--storefront-brand-primary,#059669)] hover:text-[color:var(--storefront-brand-primary,#059669)]"
            >
              {t('public.view_more_products')}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default PublicProductsSection;
